/**
 * Client for `/api/pct/v1/(documents|envelopes|analyses)` envelope endpoints. Title Case enum
 * strings (`DocuSign`, `Internal`, `Sent`, `Completed`, etc.) match
 * `.cursor/rules/enum-string-title-case.mdc`.
 */
import { API_CONFIG } from '../config';
import { pctBearerHeaders, pctEnsureOk } from './pctApiHttp';

export interface ApprovalEnvelopeRow {
  id: string;
  analysisId: string;
  analysisRunId: string;
  analysisDocumentId: string;
  provider: 'DocuSign' | 'Internal' | string;
  providerEnvelopeId: string | null;
  status: 'Created' | 'Sent' | 'Delivered' | 'Completed' | 'Declined' | 'Voided' | 'Expired' | string;
  subject: string;
  message: string | null;
  sentAt: string | null;
  completedAt: string | null;
  lastEventAt: string | null;
  createdAt: string | null;
}

export interface ApprovalEventRow {
  id: string;
  approvalEnvelopeId: string;
  eventType: string;
  recipientName: string | null;
  recipientEmail: string | null;
  occurredAt: string;
}

export interface SendForSignaturePayload {
  provider?: 'DocuSign' | 'Internal';
  emailSubject?: string;
  emailMessage?: string;
  recipients?: {
    kind?: 'DQI' | 'Shareholder' | 'Legal Counsel' | 'Other';
    name?: string;
    email?: string;
    linkedDqiClientId?: string | null;
    linkedShareholderId?: string | null;
    routingOrder?: number;
    required?: boolean;
  }[];
}

export async function sendDocumentForSignature(
  documentId: string,
  payload: SendForSignaturePayload
): Promise<ApprovalEnvelopeRow> {
  const headers = await pctBearerHeaders();
  if (!headers) throw new Error('Not signed in.');
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.documentSendForSignature(documentId)}`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  await pctEnsureOk(res, 'Failed to send for signature');
  const json = (await res.json()) as { data?: ApprovalEnvelopeRow };
  if (!json?.data) throw new Error('Empty response from server.');
  return json.data;
}

export async function listEnvelopes(analysisId: string): Promise<ApprovalEnvelopeRow[]> {
  const headers = await pctBearerHeaders();
  if (!headers) return [];
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisEnvelopes(analysisId)}`;
  const res = await fetch(url, { headers });
  await pctEnsureOk(res, 'Failed to load envelopes');
  const json = (await res.json()) as { data?: ApprovalEnvelopeRow[] };
  return json?.data ?? [];
}

export async function getEnvelope(
  envelopeId: string
): Promise<{ envelope: ApprovalEnvelopeRow; events: ApprovalEventRow[] } | null> {
  const headers = await pctBearerHeaders();
  if (!headers) return null;
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.envelope(envelopeId)}`;
  const res = await fetch(url, { headers });
  if (res.status === 404) return null;
  await pctEnsureOk(res, 'Failed to load envelope');
  const json = (await res.json()) as { data?: { envelope: ApprovalEnvelopeRow; events: ApprovalEventRow[] } };
  return json?.data ?? null;
}

export async function voidEnvelope(envelopeId: string, reason: string): Promise<ApprovalEnvelopeRow> {
  const headers = await pctBearerHeaders();
  if (!headers) throw new Error('Not signed in.');
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.envelopeVoid(envelopeId)}`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ reason }) });
  await pctEnsureOk(res, 'Failed to void envelope');
  const json = (await res.json()) as { data?: ApprovalEnvelopeRow };
  if (!json?.data) throw new Error('Empty response from server.');
  return json.data;
}

export interface ManualVotePayload {
  analysisRunId: string;
  shareholderId: string;
  decision: 'Approve' | 'Disapprove' | 'Abstain';
  source?: 'Manual' | 'Paper' | 'DocuSign';
  approvalEnvelopeId?: string | null;
  evidenceDocumentId?: string | null;
  notes?: string | null;
}

export async function recordManualVote(analysisId: string, payload: ManualVotePayload): Promise<void> {
  const headers = await pctBearerHeaders();
  if (!headers) throw new Error('Not signed in.');
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisManualVote(analysisId)}`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  await pctEnsureOk(res, 'Failed to record vote');
}

export interface VoteTallyRow {
  analysisId: string;
  analysisRunId: string;
  totalShares: number;
  excludedShares: number;
  eligibleShares: number;
  approveShares: number;
  disapproveShares: number;
  abstainShares: number;
  unvotedShares: number;
  requiredApproveSharesForApproval: number;
  approvalRatio: number;
  thresholdRatio: number;
  thresholdMet: boolean;
  outstandingShareholders: { shareholderId: string; name: string; shares: number }[];
  shareholders: VoteTallyShareholderRow[];
  computedAt: string;
}

export interface VoteTallyShareholderRow {
  shareholderId: string;
  name: string;
  shares: number;
  excluded: boolean;
  decision: 'Approve' | 'Disapprove' | 'Abstain' | null;
  recordedAt: string | null;
}

export async function getVoteTally(analysisId: string, runId: string): Promise<VoteTallyRow | null> {
  const headers = await pctBearerHeaders();
  if (!headers) return null;
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRunTally(analysisId, runId)}`;
  const res = await fetch(url, { headers });
  if (res.status === 404) return null;
  await pctEnsureOk(res, 'Failed to load tally');
  const json = (await res.json()) as { data?: VoteTallyRow };
  return json?.data ?? null;
}

/**
 * Downloads the run-scoped audit export zip (documents + manifest.json + README). The browser
 * download is initiated via a Blob URL so the JWT bearer header is honored.
 */
export async function downloadAuditExport(analysisId: string, runId: string): Promise<void> {
  const headers = await pctBearerHeaders();
  if (!headers) throw new Error('Not signed in.');
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRunAuditExport(analysisId, runId)}`;
  const res = await fetch(url, { headers: { ...(headers as Record<string, string>), Accept: 'application/zip' } });
  await pctEnsureOk(res, 'Failed to download audit export');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const fileName = match?.[1] ?? `280g-audit-${runId}.zip`;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = decodeURIComponent(fileName);
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  }
}
